import { useEffect, useRef } from 'react';

import { DialogProvider } from './components/Dialog';
import { StoreProvider, useStore } from './store';
import { primeSpeech } from './tts';
import type { Screen } from './types';
import UnitListScreen from './screens/UnitListScreen';
import AddSentencesScreen from './screens/AddSentencesScreen';
import ScenarioMenuScreen from './screens/ScenarioMenuScreen';
import CardPlayScreen from './screens/CardPlayScreen';
import DictationScreen from './screens/DictationScreen';
import ExpressionListScreen from './screens/ExpressionListScreen';

/** How deep each screen sits in the in-app back stack. */
const DEPTH: Record<Screen, number> = {
  unit_list: 0,
  scenario_menu: 1,
  add_sentences: 1,
  card_play: 2,
  dictation: 2,
  expression_list: 2,
};

function Router() {
  const s = useStore();
  const depth = DEPTH[s.screen];

  // ---- browser Back button ↔ in-app navigation ----------------------------
  // The RN build mapped Android's hardware back button via BackHandler. On the
  // web the equivalent is a history entry per level: entering a screen pushes
  // one, and a popstate means the user asked to go back. In-app back buttons
  // then have to consume the entry they pushed, which would itself fire
  // popstate — hence the short suppression window (browsers disagree on how
  // many popstate events a multi-step go() emits, so a time window is more
  // portable than counting them).
  const prevDepth = useRef(0);
  const cameFromPop = useRef(false);
  const suppressUntil = useRef(0);
  const screenRef = useRef(s.screen);
  screenRef.current = s.screen;

  const goBack = useRef<() => void>(() => {});
  goBack.current = () => {
    switch (screenRef.current) {
      case 'scenario_menu':
      case 'add_sentences':
        s.backToUnitList();
        break;
      case 'card_play':
      case 'dictation':
      case 'expression_list':
        s.backToScenarioMenu();
        break;
      default:
        break; // unit_list is the root — let the browser leave the page
    }
  };

  useEffect(() => {
    const onPop = () => {
      if (performance.now() < suppressUntil.current) return;
      cameFromPop.current = true;
      goBack.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const prev = prevDepth.current;
    prevDepth.current = depth;

    if (depth > prev) {
      for (let i = prev; i < depth; i++) window.history.pushState({ s2r: i + 1 }, '');
    } else if (depth < prev) {
      // A popstate-driven back already consumed one entry; drop only the rest.
      const steps = prev - depth - (cameFromPop.current ? 1 : 0);
      if (steps > 0) {
        suppressUntil.current = performance.now() + 500;
        window.history.go(-steps);
      }
    }
    cameFromPop.current = false;
  }, [depth]);

  // ---- iOS Safari speech unlock -------------------------------------------
  // Mobile Safari ignores speechSynthesis.speak() unless speech has been
  // started once from a genuine user gesture. Dictation and flashcards both
  // auto-play on entry, so without this the first sentence is silent.
  useEffect(() => {
    const prime = () => primeSpeech();
    window.addEventListener('pointerdown', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  if (!s.ready) {
    return (
      <div className="center-fill">
        <div className="spinner" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <>
      {!s.speechSupported && <div className="notice">{s.t('tts.unsupported_body')}</div>}
      {renderScreen(s.screen)}
    </>
  );
}

function renderScreen(screen: Screen) {
  switch (screen) {
    case 'add_sentences':
      return <AddSentencesScreen />;
    case 'scenario_menu':
      return <ScenarioMenuScreen />;
    case 'card_play':
      return <CardPlayScreen />;
    case 'dictation':
      return <DictationScreen />;
    case 'expression_list':
      return <ExpressionListScreen />;
    case 'unit_list':
    default:
      return <UnitListScreen />;
  }
}

export default function App() {
  return (
    <DialogProvider>
      <StoreProvider>
        <div className="app">
          <Router />
        </div>
      </StoreProvider>
    </DialogProvider>
  );
}
